import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { TicketAdminDashboard, DEFAULT_TICKET_TYPES, generateOrderTickets } from '../components/tickets';
import request from '../utils/request';

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA - Remove this section when backend is ready
// ═══════════════════════════════════════════════════════════════════════════

// Force real data path; mock data kept for reference but disabled.
const useMockData = false;

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);
const dayAfter = new Date(today);
dayAfter.setDate(dayAfter.getDate() + 2);

const MOCK_DISPATCHERS = [
  { _id: 'disp_001', firstName: 'Sarah', lastName: 'Johnson', role: 'support', profilePic: null },
  { _id: 'disp_002', firstName: 'Mike', lastName: 'Chen', role: 'support', profilePic: null },
  { _id: 'disp_003', firstName: 'Emily', lastName: 'Rodriguez', role: 'support', profilePic: null },
  { _id: 'disp_004', firstName: 'James', lastName: 'Wilson', role: 'admin', profilePic: null },
];

const MOCK_ORDERS = [
  {
    _id: 'order_001',
    uniqueId: '78234',
    status: 'requested',
    inStockDate: tomorrow.toISOString(),
    guestEmail: 'john.smith@example.com',
    property: { address: { city: 'Austin', state: 'TX' } },
    storeOrderConfirmationUrl: null,
    arrivalTime: null,
    entryInstructions: 'Gate code: 1234',
    driverID: null,
    storeSpend: null,
  },
  {
    _id: 'order_002',
    uniqueId: '78235',
    status: 'pending',
    inStockDate: tomorrow.toISOString(),
    guestEmail: 'sarah.jones@example.com',
    property: { address: { city: 'Dallas', state: 'TX' } },
    storeOrderConfirmationUrl: 'https://kroger.com/order/123456',
    arrivalTime: '2:00 PM - 4:00 PM',
    entryInstructions: null,
    driverID: 'driver_001',
    storeSpend: 156.78,
  },
  {
    _id: 'order_003',
    uniqueId: '78236',
    status: 'requested',
    inStockDate: today.toISOString(),
    guestEmail: 'mike.wilson@example.com',
    property: { address: { city: 'Houston', state: 'TX' } },
    storeOrderConfirmationUrl: 'https://heb.com/order/789',
    arrivalTime: '10:00 AM - 12:00 PM',
    entryInstructions: 'Leave at front door',
    driverID: null,
    storeSpend: 89.50,
  },
  {
    _id: 'order_004',
    uniqueId: '78237',
    status: 'picked',
    inStockDate: today.toISOString(),
    guestEmail: 'emily.brown@example.com',
    property: { address: { city: 'San Antonio', state: 'TX' } },
    storeOrderConfirmationUrl: 'https://walmart.com/order/456',
    arrivalTime: '3:00 PM - 5:00 PM',
    entryInstructions: 'Ring doorbell twice',
    driverID: 'driver_002',
    storeSpend: 234.00,
  },
  {
    _id: 'order_005',
    uniqueId: '78238',
    status: 'pending',
    inStockDate: dayAfter.toISOString(),
    guestEmail: 'david.lee@example.com',
    property: { address: { city: 'Fort Worth', state: 'TX' } },
    storeOrderConfirmationUrl: null,
    arrivalTime: null,
    entryInstructions: null,
    driverID: null,
    storeSpend: null,
  },
  {
    _id: 'order_006',
    uniqueId: '78239',
    status: 'requested',
    inStockDate: tomorrow.toISOString(),
    guestEmail: 'lisa.wang@example.com',
    property: { address: { city: 'Plano', state: 'TX' } },
    storeOrderConfirmationUrl: 'https://target.com/order/999',
    arrivalTime: '1:00 PM - 3:00 PM',
    entryInstructions: 'Keypad: 5678#',
    driverID: null,
    storeSpend: 178.25,
  },
];

const generateMockTicketsForAdmin = (orders, dispatchers) => {
  const tickets = [];
  let ticketIndex = 0;

  orders.forEach((order, orderIdx) => {
    const orderDate = new Date(order.inStockDate);
    orderDate.setHours(0, 0, 0, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const dayDiff = Math.ceil((orderDate - todayDate) / (1000 * 60 * 60 * 24));
    const isUrgent = dayDiff === 1;
    const isToday = dayDiff === 0;

    // Assign to different dispatchers round-robin
    const assignee = dispatchers[orderIdx % dispatchers.length];

    // Place Order ticket
    if (!order.storeOrderConfirmationUrl) {
      const status = ticketIndex < 2 ? 'in_progress' : ticketIndex < 5 ? 'completed' : 'pending';
      tickets.push({
        id: `ticket_${ticketIndex++}`,
        type: 'PLACE_ORDER',
        name: 'Place Store Order',
        category: 'placement',
        orderId: order._id,
        orderUniqueId: order.uniqueId,
        priority: isToday ? 'P0' : isUrgent ? 'P1' : 'P2',
        effort: 3,
        estimatedMinutes: 15,
        status,
        assignedTo: assignee._id,
        assignedToName: `${assignee.firstName} ${assignee.lastName}`,
        dueAt: new Date(Date.now() + (isToday ? 1 : 4) * 60 * 60 * 1000).toISOString(),
        startedAt: status === 'in_progress' ? new Date(Date.now() - 8 * 60 * 1000).toISOString() : null,
        completedAt: status === 'completed' ? new Date(Date.now() - 30 * 60 * 1000).toISOString() : null,
        completedBy: status === 'completed' ? assignee._id : null,
        completedByName: status === 'completed' ? `${assignee.firstName} ${assignee.lastName}` : null,
        durationMinutes: status === 'completed' ? Math.floor(Math.random() * 15) + 5 : null,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      });
    }

    // Set Delivery Time ticket
    if (!order.arrivalTime) {
      const status = ticketIndex < 3 ? 'completed' : 'pending';
      tickets.push({
        id: `ticket_${ticketIndex++}`,
        type: 'SET_DELIVERY_TIME',
        name: 'Set Delivery Time',
        category: 'pre_delivery',
        orderId: order._id,
        orderUniqueId: order.uniqueId,
        priority: isUrgent ? 'P1' : 'P2',
        effort: 1,
        estimatedMinutes: 5,
        status,
        assignedTo: assignee._id,
        assignedToName: `${assignee.firstName} ${assignee.lastName}`,
        dueAt: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
        completedAt: status === 'completed' ? new Date(Date.now() - 45 * 60 * 1000).toISOString() : null,
        completedBy: status === 'completed' ? assignee._id : null,
        completedByName: status === 'completed' ? `${assignee.firstName} ${assignee.lastName}` : null,
        durationMinutes: status === 'completed' ? Math.floor(Math.random() * 5) + 2 : null,
        createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      });
    }

    // Verify Entry Instructions ticket
    if (!order.entryInstructions) {
      tickets.push({
        id: `ticket_${ticketIndex++}`,
        type: 'VERIFY_ENTRY',
        name: 'Verify Entry Instructions',
        category: 'pre_delivery',
        orderId: order._id,
        orderUniqueId: order.uniqueId,
        priority: 'P2',
        effort: 1,
        estimatedMinutes: 5,
        status: 'pending',
        assignedTo: assignee._id,
        assignedToName: `${assignee.firstName} ${assignee.lastName}`,
        dueAt: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
        createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      });
    }

    // Find Driver ticket (if no driver and within 2 days)
    if (!order.driverID && dayDiff <= 2) {
      tickets.push({
        id: `ticket_${ticketIndex++}`,
        type: 'FIND_DRIVER',
        name: 'Find Concierge',
        category: 'driver',
        orderId: order._id,
        orderUniqueId: order.uniqueId,
        priority: isToday ? 'P0' : 'P1',
        effort: 5,
        estimatedMinutes: 30,
        status: ticketIndex % 4 === 0 ? 'in_progress' : 'pending',
        assignedTo: assignee._id,
        assignedToName: `${assignee.firstName} ${assignee.lastName}`,
        dueAt: new Date(Date.now() + (isToday ? 0.5 : 2) * 60 * 60 * 1000).toISOString(),
        startedAt: ticketIndex % 4 === 0 ? new Date(Date.now() - 12 * 60 * 1000).toISOString() : null,
        createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      });
    }
  });

  // Add some driver support tickets
  tickets.push({
    id: `ticket_${ticketIndex++}`,
    type: 'DRIVER_SUPPORT',
    name: 'Driver Support',
    category: 'support',
    orderId: 'order_003',
    orderUniqueId: '78236',
    priority: 'P0',
    effort: 2,
    estimatedMinutes: 10,
    status: 'in_progress',
    assignedTo: MOCK_DISPATCHERS[0]._id,
    assignedToName: `${MOCK_DISPATCHERS[0].firstName} ${MOCK_DISPATCHERS[0].lastName}`,
    isSlackTicket: true,
    dueAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
    startedAt: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
  });

  // Add an overdue ticket
  tickets.push({
    id: `ticket_${ticketIndex++}`,
    type: 'CONTACT_HOST',
    name: 'Contact Host',
    category: 'support',
    orderId: 'order_004',
    orderUniqueId: '78237',
    priority: 'P1',
    effort: 2,
    estimatedMinutes: 10,
    status: 'pending',
    assignedTo: MOCK_DISPATCHERS[1]._id,
    assignedToName: `${MOCK_DISPATCHERS[1].firstName} ${MOCK_DISPATCHERS[1].lastName}`,
    dueAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // OVERDUE
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  });

  // Add completed tickets for metrics
  for (let i = 0; i < 8; i++) {
    const assignee = dispatchers[i % dispatchers.length];
    tickets.push({
      id: `ticket_completed_${i}`,
      type: ['PLACE_ORDER', 'SET_DELIVERY_TIME', 'VERIFY_ENTRY', 'EOD_REVIEW'][i % 4],
      name: ['Place Store Order', 'Set Delivery Time', 'Verify Entry', 'EOD Review'][i % 4],
      category: 'completed_demo',
      orderId: `order_00${(i % 6) + 1}`,
      orderUniqueId: `7823${(i % 6) + 4}`,
      priority: 'P2',
      effort: [3, 1, 1, 2][i % 4],
      status: 'completed',
      assignedTo: assignee._id,
      assignedToName: `${assignee.firstName} ${assignee.lastName}`,
      completedAt: new Date(Date.now() - (i + 1) * 45 * 60 * 1000).toISOString(),
      completedBy: assignee._id,
      completedByName: `${assignee.firstName} ${assignee.lastName}`,
      durationMinutes: Math.floor(Math.random() * 20) + 5,
      createdAt: new Date(Date.now() - (i + 2) * 60 * 60 * 1000).toISOString(),
    });
  }

  return tickets;
};

// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ticket Admin Screen
 * 
 * Admin command center for:
 * - Viewing all dispatchers and their workloads
 * - Order story timelines
 * - Performance metrics
 * - Ticket reassignment and escalation
 */
export default function TicketAdminScreen() {
  const { adminInfo } = useSelector(s => s.login);
  
  const [orders, setOrders] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [dispatcherMetrics, setDispatcherMetrics] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch orders via Firebase real-time
  useEffect(() => {
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('status', 'not-in', ['completed', 'cancelled', 'delivered'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedOrders = snapshot.docs.map(doc => ({
        _id: doc.id,
        ...doc.data()
      }));
      setOrders(fetchedOrders);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching orders:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch dispatchers (employees)
  useEffect(() => {
    const fetchDispatchers = async () => {
      try {
        const res = await request.get('/employees'); // New endpoint for employees
        const allEmployees = res.data.employees || [];
        // Filter to dispatchers/support roles
        const dispatchers = allEmployees.filter(e => ['dispatcher', 'support', 'admin'].includes(e.role));
        setDispatchers(dispatchers);
      } catch (err) {
        console.error('Error fetching dispatchers:', err);
      }
    };
    
    fetchDispatchers();
  }, []);

  const [tickets, setTickets] = useState([]);

  // Fetch tickets from backend
  useEffect(() => {
    if (useMockData) return;

    const fetchTickets = async () => {
      try {
        const res = await request.get('/tickets?includeOrder=true');
        if (res.data.success) {
          // Map MongoDB _id to id and ensure orderId is a string for frontend compatibility
          const mappedTickets = res.data.tickets.map(t => ({
            ...t,
            id: t._id || t.id,
            type: t.ticketTypeId || t.type,
            orderId: t.orderId?._id || t.orderId?.toString?.() || t.orderId,
            order: t.orderId && typeof t.orderId === 'object' ? t.orderId : null,
          }));
          setTickets(mappedTickets);
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
      }
    };

    fetchTickets();
    // Poll every 30s as fallback to socket
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, [useMockData]);

  // Fetch dispatcher metrics
  const fetchDispatcherMetrics = useCallback(async () => {
    if (useMockData) return;

    try {
      const [workloadRes, leaderboardRes] = await Promise.all([
        request.get('/tickets/workload'),
        request.get('/tickets/metrics/leaderboard?period=week&limit=20')
      ]);

      if (workloadRes.data.success) {
        setDispatcherMetrics(workloadRes.data.dispatchers);
      }

      if (leaderboardRes.data.success) {
        setLeaderboard(leaderboardRes.data.leaderboard);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  }, [useMockData]);

  useEffect(() => {
    if (!useMockData) {
      fetchDispatcherMetrics();
      // Update metrics every 5 minutes
      const interval = setInterval(fetchDispatcherMetrics, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [fetchDispatcherMetrics, useMockData]);

  const mockOrders = useMemo(() => MOCK_ORDERS, []);
  const mockDispatchers = useMemo(() => MOCK_DISPATCHERS, []);

  const displayTickets = useMockData ? generateMockTicketsForAdmin(mockOrders, mockDispatchers) : tickets;
  const displayOrders = useMockData ? mockOrders : orders;
  const displayDispatchers = useMockData ? mockDispatchers : (dispatcherMetrics.length > 0 ? dispatcherMetrics : mockDispatchers);

  // Handlers
  const handleReassignTicket = useCallback(async (ticket, newAssigneeId) => {
    if (!useMockData) {
       try {
         await request.patch(`/tickets/${ticket._id || ticket.id}/reassign`, {
           assignedTo: newAssigneeId
         });
         // Refresh will happen via socket or next poll
       } catch (err) {
         console.error('Failed to reassign:', err);
       }
    }
  }, [useMockData]);

  const handleEscalateTicket = useCallback(async (ticket) => {
    if (!useMockData) {
      try {
        await request.patch(`/tickets/${ticket._id || ticket.id}/escalate`, {
          escalatedBy: adminInfo?.id || adminInfo?._id,
          reason: 'Manual escalation from Admin Dashboard'
        });
      } catch (err) {
        console.error('Failed to escalate:', err);
      }
    }
  }, [useMockData, adminInfo]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    if (!useMockData) {
        // Trigger re-fetch logic if needed, or just rely on state update
        // For now, simple timeout to simulate load
    }
    setTimeout(() => setLoading(false), 500);
  }, [useMockData]);

  if (loading && !useMockData && tickets.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading command center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-screen overflow-hidden">
      <TicketAdminDashboard
        tickets={tickets}
        orders={displayOrders}
        dispatchers={displayDispatchers}
        onReassignTicket={handleReassignTicket}
        onEscalateTicket={handleEscalateTicket}
        onRefresh={handleRefresh}
      />
    </div>
  );
}
